// External Imports
import { useEffect, useState } from "react";
import { TbChevronDown, TbChevronUp } from "react-icons/tb";
import { requestAdminAPI } from "../../api/posh-api";
import Heading from "../UI/Heading";
import DetailPair from "../UI/DetailPair";
import LoadingSpinnerWrapper from "../UI/LoadingSpinnerWrapper";
import { format } from "date-fns";
import { truncate } from "../../util/format-text";
import { isoDateToString } from "../../util/format-date";

// Internal Imports

/** ========================================================================
 * * BookingMetrics Component
 * This component renders statistics for a booking.
 * ========================================================================== */

export default function BookingMetrics({ booking, setInvoice }) {
  // Initialize state
  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState(false);
  const [metrics, setMetrics] = useState({});
  const [message, setMessage] = useState();

  /* ======================= FETCH VEHICLE REPORT ======================= */
  const generateMetrics = async () => {
    const result = {};
    try {
      setLoading(true);
      setMessage();
      const response = await requestAdminAPI("fetch-vehicle-reports", {
        vehicles: [
          { vin: booking.vehicle.vin, trackerId: booking.vehicle.trackerId },
        ],
      });
      const report = response[0]?.report;
      if (report) {
        result.current = report.mileage;
        const extraMiles =
          report.mileage - booking.startMileage - booking.milesIncluded;
        result.driven = report.mileage - booking.startMileage;
        result.extra = extraMiles > 0 ? extraMiles : 0;
        result.charge =
          extraMiles > 0 ? (extraMiles * booking.mileageRate).toFixed(2) : 0;
      } else throw new Error(response[0].error);
      setLoading(false);
      setMetrics(result);
    } catch (err) {
      setLoading(false);
      setMessage("Issues with Tracker!");
      console.error(err);
      setMetrics(result);
    }
  };

  useEffect(() => {
    if (show) generateMetrics();
  }, [show]);

  return (
    <div className="grid gap-4">
      <Heading
        title="Metrics"
        text="Vehicle usage information collected from Samsara"
        onClick={() => setShow(!show)}
        isBackArrow={false}
      >
        <p className="text-sm text-yellow-500">{truncate(message)}</p>
        {show ? (
          <TbChevronUp className="icon" onClick={() => setShow(false)} />
        ) : (
          <TbChevronDown className="icon" onClick={() => setShow(true)} />
        )}
      </Heading>
      {show && (
        <div className="px-4 info lg:grid-cols-5">
          <DetailPair
            label="Current Mileage"
            value={`${metrics.current || "-"}mi`}
            loading={loading}
          />
          <DetailPair
            label="Miles Driven"
            value={`${metrics.driven}mi`}
            loading={loading}
          />
          <DetailPair
            label="Extra Miles"
            value={`${metrics.extra}mi`}
            loading={loading}
          />
          <DetailPair
            label="Extra Mileage Fee"
            value={`$${metrics.charge}`}
            loading={loading}
          />
          {metrics.charge > 0 && (
            <div
              onClick={() =>
                setInvoice({
                  amount: metrics.charge,
                  category: "miles",
                  description: `${booking.vehicle.year} ${
                    booking.vehicle.make.name
                  } ${booking.vehicle.model} (${isoDateToString(
                    booking.startDate
                  )}-${isoDateToString(booking.endDate)}) - Extra Miles ${
                    metrics.extra
                  }mi`,
                })
              }
              className="self-end btn-secondary"
            >
              Send Invoice
            </div>
          )}
        </div>
      )}
    </div>
  );
}
